// src/components/WhatsNewModal.tsx
import { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ProductUpdate } from '@/types';

interface WhatsNewModalProps {
  open: boolean;
  items: ProductUpdate[];
  readIds: Set<string>;
  onOpenChange: (open: boolean) => void;
  onView: (id: string) => void;
}

/** Extract a YouTube video id from watch/share/embed URLs. */
function youtubeEmbedUrl(url: string): string | null {
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]{11})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
}

export const WhatsNewModal: React.FC<WhatsNewModalProps> = ({ open, items, readIds, onOpenChange, onView }) => {
  const [active, setActive] = useState(0);

  // On open (or when the item set changes) reset to the first entry and mark it viewed.
  useEffect(() => {
    if (!open || items.length === 0) return;
    setActive(0);
    onView(items[0].id);
  }, [open, items, onView]);

  const select = (i: number) => {
    setActive(i);
    if (items[i]) onView(items[i].id);
  };

  const current = items[active];
  const isLast = active >= items.length - 1;
  const embed = current?.youtubeUrl ? youtubeEmbedUrl(current.youtubeUrl) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl h-[560px] p-0 gap-0 overflow-hidden flex flex-col md:grid md:grid-cols-[264px_1fr] md:grid-rows-1">
        <DialogTitle className="sr-only">Neue Funktionen</DialogTitle>
        {items.length === 0 ? (
          <div className="grid place-items-center p-10 text-sm text-muted-foreground">Noch keine Neuigkeiten.</div>
        ) : (
          <>
            {/* Left list — hidden on mobile */}
            <aside className="hidden md:flex flex-col bg-muted border-r border-border py-6 overflow-y-auto">
              <p className="px-5 mb-3 text-xl font-semibold">Neue Funktionen</p>
              {items.map((u, i) => (
                <button
                  key={u.id}
                  onClick={() => select(i)}
                  className={`flex items-start gap-2.5 px-5 py-3 text-left border-l-[3px] transition-colors ${
                    i === active
                      ? 'bg-card border-primary font-semibold text-foreground'
                      : 'border-transparent text-muted-foreground hover:bg-black/[0.03] hover:text-foreground'
                  }`}
                >
                  <span className="flex-1 text-sm leading-snug">{u.title}</span>
                  {!readIds.has(u.id) && <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary" />}
                </button>
              ))}
            </aside>

            {/* Right detail */}
            <div className="flex flex-col min-w-0 min-h-0 flex-1">
              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
                {embed ? (
                  <div className="aspect-video w-full bg-black">
                    <iframe
                      className="h-full w-full"
                      src={embed}
                      title={current.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                ) : current.coverImageUrl ? (
                  <img src={current.coverImageUrl} alt={current.title} className="aspect-video w-full object-cover bg-muted" />
                ) : (
                  <div className="aspect-video w-full bg-muted" />
                )}
                <div className="px-6 py-5">
                  <h3 className="text-xl font-semibold mb-2.5">{current.title}</h3>
                  {current.bodyMd.trim() && (
                    <div className="text-sm leading-relaxed text-muted-foreground space-y-3 [&_h2]:text-lg [&_h2]:font-semibold [&_h2]:text-foreground [&_h3]:font-semibold [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_a]:text-primary [&_a]:underline [&_strong]:font-semibold [&_img]:rounded-lg [&_img]:my-2">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{current.bodyMd}</ReactMarkdown>
                    </div>
                  )}
                </div>
              </div>

              {/* Footer */}
              <footer className="flex items-center justify-between border-t border-border px-5 h-[62px] flex-shrink-0">
                <div className="flex gap-1.5">
                  {items.map((u, i) => (
                    <span key={u.id} className={`h-1.5 rounded-full transition-all ${i === active ? 'w-5 bg-primary' : 'w-1.5 bg-border'}`} />
                  ))}
                </div>
                <Button onClick={() => (isLast ? onOpenChange(false) : select(active + 1))}>
                  {isLast ? 'Fertig' : 'Weiter'}
                </Button>
              </footer>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
